// What each example video says, word for word (2026-10-30): crawlers and AI answers cite words, not video, so the
// gallery shows each example's narration under it (collapsed) and gives it to the video's VideoObject (`transcript`),
// and the topic and legend pages give it to the VideoObject of the video they play.
//
// exampleTranscripts.<locale>.json (es, pt, en; ar, fr, de, tr since lane GX, 2026-10-30), keyed "<locale>/<id>": `text` is the narration, one paragraph per
// scene, and `source` says where it was read: "project" (the AI Agent project's own scene narration, read through the
// API), "polished" (the narration of the hand-polished render, ~/dev/example-polish/data/<pid8>/props_final.json or
// props_app.json) or "transcribed" (the published mp4, transcribed). Every one was checked against the published
// video's sound (a Gemini transcription of the mp4, compared word by word): the only differences were numbers written
// as digits, accents and quote marks (in Arabic, the transcription's Modern Standard spellings of the Egyptian words:
// تاني/ثاني, بكتير/بكثير). A remade or re-voiced example needs its transcript read again.
//
// One file per language, as the topic pages' copy is (growth/content/localeContent.js): a topic or legend page loads
// its own language's transcripts with its copy (nicheContent.<locale>.js), ~7 KB compressed, not all three (~20 KB).
// The gallery shows several languages and loads all of them (exampleTranscriptsAll.js). This module holds only what
// has been loaded, so it costs a page nothing by itself.
export const TRANSCRIPTS = {};

/** Keep a language's transcripts once they have loaded. */
export function registerTranscripts(transcripts) {
  Object.assign(TRANSCRIPTS, transcripts || {});
}

/** The narration of a published example, one paragraph per scene, or undefined (none, or not loaded). */
export function transcriptFor(example) {
  return example ? TRANSCRIPTS[`${example.locale}/${example.id}`]?.text : undefined;
}

/** The narration as one line, for a VideoObject's `transcript`. */
export function transcriptLine(example) {
  const text = transcriptFor(example);
  return text ? text.split(/\n+/).join(' ') : undefined;
}

/** The paragraphs of a transcript. */
export const transcriptParagraphs = (text) => text.split(/\n\n+/).filter(Boolean);

/**
 * The opening of a transcript: its first sentences up to at least 40 characters ("Three words." alone says nothing).
 * A sentence ends at . ! ? ؟ followed by a space and a capital letter or digit (or ¿ ¡ or a quote), so "las noches...
 * no es" stays one sentence. A script with no capitals (Arabic) ends a sentence before any of its letters.
 */
export function firstSentence(text, min = 40) {
  const line = text.split(/\n+/).join(' ');
  const ends = /[.!?…؟]["'”’»]?(?=\s+["'“‘«¿¡]?[\p{Lu}\p{Lo}\d])/gu;
  let match;
  while ((match = ends.exec(line))) {
    const end = match.index + match[0].length;
    if (end >= min) return line.slice(0, end);
  }
  return line;
}
